import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Lock, AlertCircle, Loader, Eye, EyeOff, CheckCircle, Clock } from "lucide-react";
import { toast } from "react-hot-toast";
import { resetPassword, validateResetToken } from "../../services/auth.service";
import { validateAllMessages, isPassword } from "../../utils/validation";
import { VALIDATION_MESSAGES, TOAST_MESSAGES } from "../../constants/messages";

const validationSchema = {
  newPassword: [
    { test: (v: string) => v.trim().length > 0, message: VALIDATION_MESSAGES.password.required },
    { test: (v: string) => v.length >= 8, message: VALIDATION_MESSAGES.password.minLength },
    isPassword(VALIDATION_MESSAGES.password.invalid),
  ],
  confirmPassword: [
    { test: (v: string) => v.trim().length > 0, message: VALIDATION_MESSAGES.confirmPassword.required },
  ],
};

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string[]>>({
    newPassword: [],
    confirmPassword: [],
  });
  const [touched, setTouched] = useState<Record<string, boolean>>({
    newPassword: false,
    confirmPassword: false,
  });
  const [apiError, setApiError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  // AC Scenario 3: tracks whether the link has expired (distinct from invalid/used)
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    const checkToken = async () => {
      if (!token) {
        setIsValidating(false);
        return;
      }
      try {
        const result = await validateResetToken(token);
        const data = result.data;
        if (data?.valid) {
          setTokenValid(true);
        } else {
          setTokenValid(false);
          if (data?.reason === "expired") {
            setExpired(true);
          }
        }
      } catch {
        setTokenValid(false);
      } finally {
        setIsValidating(false);
      }
    };
    checkToken();
  }, [token]);

  const validateField = (field: string, value: string): string[] => {
    const rules = validationSchema[field as keyof typeof validationSchema];
    if (!rules) return [];
    const fieldErrors: string[] = [];
    for (const rule of rules) {
      if (!rule.test(value)) {
        fieldErrors.push(rule.message);
      }
    }
    return fieldErrors;
  };

  const handleChange = (field: string, value: string) => {
    if (field === "newPassword") setNewPassword(value);
    else setConfirmPassword(value);

    setApiError("");

    if (touched[field]) {
      const fieldErrors = validateField(field, value);
      if (field === "confirmPassword" && value !== newPassword) {
        fieldErrors.push(VALIDATION_MESSAGES.confirmPassword.mismatch);
      }
      setErrors((prev) => ({ ...prev, [field]: fieldErrors }));
    }
  };

  const handleBlur = (field: string) => {
    const value = field === "newPassword" ? newPassword : confirmPassword;
    setTouched((prev) => ({ ...prev, [field]: true }));
    const fieldErrors = validateField(field, value);
    if (field === "confirmPassword" && value !== newPassword) {
      fieldErrors.push(VALIDATION_MESSAGES.confirmPassword.mismatch);
    }
    setErrors((prev) => ({ ...prev, [field]: fieldErrors }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError("");

    const allErrors = validateAllMessages({ newPassword, confirmPassword }, validationSchema);
    if (confirmPassword !== newPassword) {
      allErrors.confirmPassword = [
        ...(allErrors.confirmPassword || []),
        VALIDATION_MESSAGES.confirmPassword.mismatch,
      ];
    }
    setErrors(allErrors);
    setTouched({ newPassword: true, confirmPassword: true });

    const hasErrors = Object.values(allErrors).some((msgs) => msgs.length > 0);
    if (hasErrors) return;

    if (!token) {
      setApiError("Invalid reset link");
      return;
    }

    setIsLoading(true);
    try {
      await resetPassword(token, newPassword);
      setIsSuccess(true);
      toast.success(TOAST_MESSAGES.success.resetPassword);
    } catch (err: any) {
      // AC Scenario 3: backend returns 410 for expired tokens
      if (err?.response?.status === 410) {
        setTokenValid(false);
        setExpired(true);
        return;
      }
      const msg = err?.response?.data?.message || TOAST_MESSAGES.error.resetPasswordFailed;
      setApiError(msg);
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Loading — validating token on mount ────────────────────────────────────
  if (isValidating) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-gray-50">
        <Loader className="h-8 w-8 text-primary-600 animate-spin" />
      </div>
    );
  }

  // ─── AC Scenario 3: Expired token ───────────────────────────────────────────
  if (expired) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full space-y-8 py-12">
          <div className="text-center">
            <img
              src="/logo/DiagnomIQ.png"
              alt="DiagnomIQ"
              className="mx-auto h-24 w-auto object-contain"
            />
          </div>
          <div className="bg-white py-8 px-6 shadow-xl rounded-lg border border-gray-100">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-amber-100">
                <Clock className="h-6 w-6 text-amber-600" />
              </div>
              <h2 className="mt-4 text-lg font-semibold text-gray-900">Reset link has expired</h2>
              <p className="mt-2 text-sm text-gray-600">
                This password reset link has expired. Reset links are only valid for a limited time.
                Please request a new one.
              </p>
            </div>
            <div className="mt-6">
              <Link
                to="/forgot-password"
                className="w-full flex justify-center py-2.5 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-500 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
              >
                Request new reset link
              </Link>
            </div>
            <div className="mt-4 text-center">
              <Link
                to="/login"
                className="text-sm text-primary-600 hover:text-primary-500 font-medium"
              >
                Back to Sign in
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── AC Scenario 2: Invalid / missing / already-used token ──────────────────
  if (!token || !tokenValid) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full space-y-8 py-12">
          <div className="text-center">
            <img
              src="/logo/DiagnomIQ.png"
              alt="DiagnomIQ"
              className="mx-auto h-24 w-auto object-contain"
            />
          </div>
          <div className="bg-white py-8 px-6 shadow-xl rounded-lg border border-gray-100">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <h2 className="mt-4 text-lg font-semibold text-gray-900">Invalid reset link</h2>
              <p className="mt-2 text-sm text-gray-600">
                This password reset link is invalid or has already been used. Please request a new one.
              </p>
            </div>
            <div className="mt-6">
              <Link
                to="/forgot-password"
                className="w-full flex justify-center py-2.5 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-500 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
              >
                Request new reset link
              </Link>
            </div>
            <div className="mt-4 text-center">
              <Link
                to="/login"
                className="text-sm text-primary-600 hover:text-primary-500 font-medium"
              >
                Back to Sign in
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── AC Scenario 1: Success state after password reset ──────────────────────
  if (isSuccess) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full space-y-8 py-12">
          <div className="text-center">
            <img
              src="/logo/DiagnomIQ.png"
              alt="DiagnomIQ"
              className="mx-auto h-24 w-auto object-contain"
            />
          </div>
          <div className="bg-white py-8 px-6 shadow-xl rounded-lg border border-gray-100">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <h2 className="mt-4 text-lg font-semibold text-gray-900">Password reset successful</h2>
              <p className="mt-2 text-sm text-gray-600">
                Your password has been updated. You can now sign in with your new password.
              </p>
            </div>
            <div className="mt-6">
              <button
                id="reset-success-signin-btn"
                onClick={() => navigate("/login")}
                className="w-full flex justify-center py-2.5 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-500 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
              >
                Sign in
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Main form — valid token, awaiting password entry ───────────────────────
  return (
    <div className="h-screen w-full flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full space-y-8 py-12">
        <div className="text-center">
          <img
            src="/logo/DiagnomIQ.png"
            alt="DiagnomIQ"
            className="mx-auto h-24 w-auto object-contain"
          />
          <h2 className="mt-4 text-xl font-bold text-gray-900">Reset your password</h2>
          <p className="mt-2 text-sm text-gray-600">Enter your new password below.</p>
        </div>

        <div className="bg-white py-8 px-6 shadow-xl rounded-lg border border-gray-100">
          {apiError && (
            <div
              id="reset-api-error"
              className="mb-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2"
              role="alert"
            >
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {apiError}
            </div>
          )}

          <form id="reset-password-form" className="space-y-6" onSubmit={handleSubmit} noValidate>
            {/* New Password */}
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                New Password
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="newPassword"
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => handleChange("newPassword", e.target.value)}
                  onBlur={() => handleBlur("newPassword")}
                  className={`pl-10 pr-10 py-3 block w-full rounded-md sm:text-sm transition-colors ${
                    touched.newPassword && errors.newPassword?.length
                      ? "border-red-300 text-red-900 placeholder-red-400 focus:ring-red-500 focus:border-red-500"
                      : "border-neutral-300 focus:ring-primary-300 focus:border-primary-500"
                  }`}
                  placeholder="Enter new password"
                  autoComplete="new-password"
                  aria-invalid={!!(touched.newPassword && errors.newPassword?.length)}
                  aria-describedby={errors.newPassword?.length ? "newPassword-error" : undefined}
                />
                <button
                  type="button"
                  id="toggle-new-password-visibility"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                  aria-label={showNewPassword ? "Hide password" : "Show password"}
                >
                  {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {touched.newPassword && errors.newPassword?.length > 0 && (
                <div id="newPassword-error" className="mt-1 space-y-0.5" role="alert">
                  {errors.newPassword.map((msg, i) => (
                    <p key={i} className="text-xs text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3 flex-shrink-0" />
                      {msg}
                    </p>
                  ))}
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm Password
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => handleChange("confirmPassword", e.target.value)}
                  onBlur={() => handleBlur("confirmPassword")}
                  className={`pl-10 pr-10 py-3 block w-full rounded-md sm:text-sm transition-colors ${
                    touched.confirmPassword && errors.confirmPassword?.length
                      ? "border-red-300 text-red-900 placeholder-red-400 focus:ring-red-500 focus:border-red-500"
                      : "border-neutral-300 focus:ring-primary-300 focus:border-primary-500"
                  }`}
                  placeholder="Confirm new password"
                  autoComplete="new-password"
                  aria-invalid={!!(touched.confirmPassword && errors.confirmPassword?.length)}
                  aria-describedby={
                    errors.confirmPassword?.length ? "confirmPassword-error" : undefined
                  }
                />
                <button
                  type="button"
                  id="toggle-confirm-password-visibility"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {touched.confirmPassword && errors.confirmPassword?.length > 0 && (
                <div id="confirmPassword-error" className="mt-1 space-y-0.5" role="alert">
                  {errors.confirmPassword.map((msg, i) => (
                    <p key={i} className="text-xs text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3 flex-shrink-0" />
                      {msg}
                    </p>
                  ))}
                </div>
              )}
            </div>

            <div>
              <button
                id="reset-password-submit-btn"
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-2.5 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-500 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <Loader className="h-4 w-4 mr-2 animate-spin" />
                    Resetting password...
                  </>
                ) : (
                  "Reset password"
                )}
              </button>
            </div>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-sm text-primary-600 hover:text-primary-500 font-medium"
            >
              Back to Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
